import katex from "katex";
import { CodeBlock } from "@/components/CodeBlock";

/* Tiny markdown-ish renderer for chat messages and AI notes.
 *  - ``` ```        → CodeBlock (with Run button via Pyodide / JDoodle)
 *  - **bold**       → <strong>
 *  - $$ math $$     → KaTeX display block
 *  - $ math $       → KaTeX inline
 *  - \( math \)     → KaTeX inline (LaTeX-style)
 *  - \[ math \]     → KaTeX display (LaTeX-style)
 *
 * We split on a single regex with capturing groups so order is preserved.
 * KaTeX rendering errors are swallowed; the raw source falls back to text.
 */

const SPLIT_RE =
  // 1: fenced code   2: bold   3: SVG block   4: $$...$$   5: \[...\]   6: $...$   7: \(...\)
  /(```[\s\S]*?```|\*\*[^*]+\*\*|<svg[\s\S]*?<\/svg>|\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\$[^$\n]+?\$|\\\([\s\S]+?\\\))/g;

// Crude allow-list sanitizer for AI-emitted SVGs: keep only safe tags + attrs.
// SVG is rendered via dangerouslySetInnerHTML so we strip <script> and on* attrs.
const sanitizeSvg = (svg) =>
  svg
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/\sxlink:href\s*=\s*("javascript:[^"]*"|'javascript:[^']*')/gi, "");

const renderSvg = (svg) => (
  <span
    className="brimi-svg-block"
    dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }}
  />
);

const renderMath = (src, displayMode) => {
  try {
    const html = katex.renderToString(src, {
      displayMode,
      throwOnError: false,
      output: "html",
      strict: "ignore",
    });
    return (
      <span
        className={displayMode ? "brimi-math-block" : "brimi-math-inline"}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  } catch {
    return <span className="font-mono text-destructive">{src}</span>;
  }
};

export function renderMarkdown(text) {
  if (!text) return null;
  const parts = text.split(SPLIT_RE);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.startsWith("```")) {
      const match = p.match(/^```(\w+)?\n?([\s\S]*?)```$/);
      const language = (match && match[1]) || "";
      const code = match && match[2]
        ? match[2].replace(/\n$/, "")
        : p.replace(/```(\w+)?\n?/, "").replace(/```$/, "");
      return <CodeBlock key={i} code={code} language={language} />;
    }
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith("<svg") && p.endsWith("</svg>")) {
      return <span key={i}>{renderSvg(p)}</span>;
    }
    if (p.startsWith("$$") && p.endsWith("$$")) {
      return <span key={i}>{renderMath(p.slice(2, -2).trim(), true)}</span>;
    }
    if (p.startsWith("\\[") && p.endsWith("\\]")) {
      return <span key={i}>{renderMath(p.slice(2, -2).trim(), true)}</span>;
    }
    if (p.startsWith("$") && p.endsWith("$") && p.length > 2) {
      return <span key={i}>{renderMath(p.slice(1, -1).trim(), false)}</span>;
    }
    if (p.startsWith("\\(") && p.endsWith("\\)")) {
      return <span key={i}>{renderMath(p.slice(2, -2).trim(), false)}</span>;
    }
    return <span key={i}>{p}</span>;
  });
}
